// Центральная зона: 1–4 колонки вариантов. Здесь же — фон рабочей области как drop-зона
// «убрать из варианта» и явная зона, которая появляется во время перетаскивания элемента
// уже принадлежащего какому-то варианту (см. правила dnd в CONTRACT.md/ТЗ).
import { useMemo, type DragEvent } from 'react';
import { type VariantTotals } from '../../types';
import { computeVariantTotals } from '../../core';
import { useActions, useStore } from '../../store';
import { useT } from '../../i18n';
import { useAuth } from '../../auth';
import { SEGMENT_DND_MIME } from '../library';
import { useIsMobile } from '../shell';
import { TripTemplates, withTemplate, type TripTemplate } from '../../templates';
import { MagicButton } from '../magic';
import { resolveDrop, useDnd, type DragPayload } from '../dnd';
import { VariantColumn } from './VariantColumn';
import styles from './VariantsBoard.module.css';

export function VariantsBoard() {
  const state = useStore();
  const actions = useActions();
  const dnd = useDnd();
  const t = useT();
  const { isGuest, maxVariants, requestSignIn } = useAuth();
  const isMobile = useIsMobile();
  // D13: рядом с «Магией» кнопке «+ Вариант» на мобильном достаётся лишь
  // ~20% ширины ряда (см. .addRow/.emptyActions ниже) — полная подпись туда
  // не влезает. Сокращаем видимый текст, но не доступное имя: aria-label
  // остаётся полным, чтобы скринридер не услышал голый «+».
  const addVariantLabel = isMobile ? t('variant.add.short') : t('variant.add');

  // Предупреждения расчёта — тоже текст интерфейса, поэтому пересчитываем их
  // при смене языка.
  const totalsById = useMemo(() => {
    const map = new Map<string, VariantTotals>();
    for (const v of state.variants) map.set(v.id, computeVariantTotals(v, state, t));
    return map;
  }, [state, t]);

  const showRemoveZone = !!dnd.drag?.fromVariantId;
  // Магии нечего собирать без рейсов в библиотеке — кнопка появляется вместе
  // с первым из них, а не висит заранее глухой.
  const hasFlights = useMemo(
    () => Object.values(state.library).some((seg) => seg.kind === 'flight'),
    [state.library],
  );
  // Гостю доступно меньше вариантов, чем вошедшему: предел приходит из auth.
  const atMax = state.variants.length >= maxVariants;
  const guestLimited = atMax && isGuest;

  function readPayload(e: DragEvent<HTMLDivElement>): DragPayload | null {
    const segmentId = e.dataTransfer.getData(SEGMENT_DND_MIME) || e.dataTransfer.getData('text/plain');
    return dnd.drag ?? (segmentId ? { segmentId, fromVariantId: null, fromIndex: null } : null);
  }

  function onBoardDragOver(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
  }

  function onBoardDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    const payload = readPayload(e);
    if (payload) resolveDrop(actions, payload, null);
    dnd.end();
  }

  function onRemoveZoneDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    const payload = readPayload(e);
    if (payload) resolveDrop(actions, payload, null);
    dnd.end();
  }

  /** Шаблон приходит вариантом целиком, поэтому меняется всё состояние разом. */
  function applyTemplate(tpl: TripTemplate) {
    actions.replaceState(withTemplate(state, tpl, t));
  }

  if (state.variants.length === 0) {
    return (
      <>
        {/* Заголовок карточки — только на мобильном (D12): три зоны ленты там
            разъехались в отдельные карточки, и у «Библиотеки» с «Итогом» уже
            есть своя подпись, у канваса её не было вовсе. На десктопе скрыт
            через CSS — секции хватает aria-label из App.tsx. */}
        <h2 className={styles.boardTitle}>{t('board.title')}</h2>
        <div
          className={styles.board}
          data-drop-remove="true"
          onDragOver={onBoardDragOver}
          onDrop={onBoardDrop}
        >
          <div className={styles.emptyBoard}>
            <p className={styles.emptyTitle}>{t('variant.empty.title')}</p>
            <p className={styles.emptyHint}>{t('variant.empty.hint')}</p>
            {/* Пустой вариант руками и магия стоят рядом: первый — обычный путь,
                вторая собирает варианты из уже готовой библиотеки, поэтому она
                и появляется только там, где библиотеке есть что предложить. */}
            <div className={styles.emptyActions}>
              <button
                type="button"
                className="btn btn-primary"
                aria-label={isMobile ? t('variant.add') : undefined}
                onClick={() => actions.addVariant(t('variant.defaultName', { n: state.variants.length + 1 }))}
              >
                {addVariantLabel}
              </button>
              {hasFlights && <MagicButton />}
            </div>

            {/* Пустой вариант руками — по-прежнему главный путь, поэтому шаблоны
                стоят ниже кнопки и раскрываются сами только в совсем новой
                поездке, где и библиотека пуста. */}
            <div className={styles.emptyTemplates}>
              <TripTemplates
                actionKey="tpl.fill"
                noteKey="tpl.board.note"
                defaultOpen={Object.keys(state.library).length === 0}
                onPick={applyTemplate}
              />
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {/* См. комментарий у пустого канваса выше — тот же заголовок, показывается
          только на мобильном (D12). */}
      <h2 className={styles.boardTitle}>{t('board.title')}</h2>
      <div className={styles.wrap}>
        <div className={styles.board} data-drop-remove="true" onDragOver={onBoardDragOver} onDrop={onBoardDrop}>
          {state.variants.map((v, i) => (
            <VariantColumn
              key={v.id}
              variant={v}
              totals={totalsById.get(v.id)!}
              index={i}
              totalCount={state.variants.length}
            />
          ))}

          {/* Кнопки живут внутри скроллящейся ленты, сразу под последним вариантом:
              добавление продолжает список, а не висит отдельным элементом сверху.
              «+ Вариант» — первая слева (D13): на мобильном ей достаётся узкая
              ~20% полоса ряда, «Магии» — широкая остальная часть. */}
          <div className={styles.addRow}>
            <button
              type="button"
              className={`btn ${styles.addBtn}`}
              disabled={atMax && !guestLimited}
              aria-label={isMobile ? t('variant.add') : undefined}
              title={
                guestLimited
                  ? t('variant.add.guest', { count: maxVariants })
                  : atMax
                    ? t('variant.add.max', { count: maxVariants })
                    : undefined
              }
              onClick={() => {
                // Упёрся в гостевой предел — предлагаем вход, а не глухую кнопку.
                if (guestLimited) return requestSignIn();
                actions.addVariant(t('variant.defaultName', { n: state.variants.length + 1 }));
              }}
            >
              {addVariantLabel}
            </button>
            {hasFlights && <MagicButton className={styles.addBtn} />}
          </div>
        </div>
        {showRemoveZone && (
          <div
            className={styles.removeZone}
            data-drop-remove="true"
            onDragOver={(e) => e.preventDefault()}
            onDrop={onRemoveZoneDrop}
          >
            {t('timeline.dropToRemove')}
          </div>
        )}
      </div>
    </>
  );
}
